import { Key } from 'react';
import {
  TableState as OriginalTableState,
  TableStateProps,
  useTableState as useOriginalTableState,
} from 'react-stately/useTableState';
import { getChildNodes } from 'react-stately/private/collections/getChildNodes';
import { SapphireTableCollection } from './SapphireTableCollection';
import patchSelectionManager from '../../utils/patchSelectionManager';
import { Selection } from '@react-types/shared/src/selection';
import { SelectionManager } from 'react-stately/private/selection/SelectionManager';
import { Collection, Node } from '@react-types/shared';

export interface SapphireTableState<T>
  extends Omit<OriginalTableState<T>, 'collection'> {
  collection: SapphireTableCollection<T>;
}

export type SapphireTableStateProps<T extends object> = Omit<
  TableStateProps<T>,
  'collection' | 'onSelectionChange'
> & {
  collection: SapphireTableCollection<T>;
  /**
   * Disables selection and row action on the entire table
   * @default false
   */
  isDisabled?: boolean;
  onSelectionChange?: (selection: Selection) => void;
  resolveSelectAllToKeys?: boolean;
};

export function useTableState<T extends object>({
  isDisabled = false,
  resolveSelectAllToKeys = false,
  collection,
  onSelectionChange,
  ...props
}: SapphireTableStateProps<T>): SapphireTableState<T> {
  const state = useOriginalTableState({
    onSelectionChange,
    ...props,
    children: undefined,
    collection,
  });

  const selectionManager = state.selectionManager as SelectionManager & {
    [ORIGINAL_CAN_SELECT_ITEM_IN]?: SelectionManager['canSelectItemIn'];
  };
  patchSelectionManager(state.selectionManager, {
    resolveSelectAllToKeys,
    isDisabled,
  });

  // patch canSelectItemIn to support nonSelectable prop and exclude footer rows from selection.
  selectionManager[ORIGINAL_CAN_SELECT_ITEM_IN] =
    selectionManager[ORIGINAL_CAN_SELECT_ITEM_IN] ??
    // @ts-expect-error ⚠️ accessing private field ⚠️
    state.selectionManager.canSelectItemIn.bind(selectionManager);
  // @ts-expect-error ⚠️ accessing private field ⚠️
  selectionManager.canSelectItemIn = function (
    key: Key,
    collection: Collection<Node<unknown>>
  ): boolean {
    const footerRows = [
      ...getChildNodes(
        (collection as SapphireTableCollection<unknown>).footer,
        collection
      ),
    ];
    const isFooterRow = footerRows.find((row) => row.key === key);
    const item = collection.getItem(key);
    return (
      !isDisabled &&
      !item?.props.INTERNAL_nonSelectable &&
      !isFooterRow &&
      selectionManager[ORIGINAL_CAN_SELECT_ITEM_IN]!(key, collection)
    );
  };

  return { ...state, collection };
}

const ORIGINAL_CAN_SELECT_ITEM_IN = Symbol('ORIGINAL_CAN_SELECT_ITEM_IN');
